import { type ComponentProps, useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { fuzzyMatch } from '../../../common/functions/fuzzy-match';
import { useDialogContainer } from '../../../common/hooks/use-dialog-container';
import {
  Combobox,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxMultipleLabel,
  type ComboboxPresetBaseProps,
  type ComboboxRootProps,
  ComboboxValue,
  useComboboxAnchor,
} from './combobox';
import { buildVirtualizedHighlightHandler, VirtualizedList, type VirtualizerHandle } from './virtualized-internals';

export interface StaticMultiComboboxProps<T>
  extends ComboboxPresetBaseProps<T>,
    Pick<ComboboxRootProps<T, true>, 'value' | 'onValueChange' | 'isItemEqualToValue' | 'disabled'>,
    Pick<ComponentProps<typeof ComboboxChipsInput>, 'placeholder'>,
    Omit<ComponentProps<typeof ComboboxChips>, 'value' | 'onValueChange' | 'children'> {
  /** Whether `items` is currently in-flight. When true and `items` is empty, the empty slot renders a loading message instead of `emptyMessage`. */
  isLoading?: boolean;
}

export const StaticMultiCombobox = <T,>({
  items,
  value,
  onValueChange,
  getKey,
  getLabel,
  isItemEqualToValue,
  placeholder,
  emptyMessage,
  container,
  disabled,
  isLoading = false,
  estimateSize,
  id,
  collapseMultiple = false,
  ...chipsProps
}: StaticMultiComboboxProps<T>) => {
  const { t } = useTranslation();
  const anchor = useComboboxAnchor();
  const resolvedContainer = useDialogContainer(anchor);
  const virtualizerRef = useRef<VirtualizerHandle | null>(null);
  const onItemHighlighted = useMemo(() => buildVirtualizedHighlightHandler(virtualizerRef), []);

  // Stash getLabel behind a ref so `filter` and `stableItemToStringLabel` stay referentially stable
  // even when callers inline `getLabel`. Base-ui has both as memo deps; an unstable identity re-runs
  // the filter over all items on every parent re-render.
  const getLabelRef = useRef(getLabel);
  getLabelRef.current = getLabel;

  const filter = useCallback((item: T, query: string) => fuzzyMatch(getLabelRef.current(item), query), []);
  const stableItemToStringLabel = useCallback((item: T) => getLabelRef.current(item), []);

  // Default to key-based equality so cloned values (e.g. round-tripped through react-hook-form) still match items.
  const defaultIsItemEqualToValue = useCallback((item: T, v: T) => getKey(item) === getKey(v), [getKey]);
  const effectiveIsItemEqualToValue = isItemEqualToValue ?? defaultIsItemEqualToValue;

  // Loading copy only during the initial fetch (items is empty) so background refetches and
  // filter-to-empty results keep showing the real emptyMessage.
  const showLoadingCopy = isLoading && !items?.length;

  return (
    <Combobox
      multiple
      virtualized
      items={items}
      value={value}
      onValueChange={onValueChange}
      disabled={disabled}
      itemToStringLabel={stableItemToStringLabel}
      isItemEqualToValue={effectiveIsItemEqualToValue}
      filter={filter}
      onItemHighlighted={onItemHighlighted}
    >
      <ComboboxChips ref={anchor} collapseMultiple={collapseMultiple} {...chipsProps}>
        <ComboboxValue>
          {collapseMultiple && (value ?? []).length > 1 ? (
            <ComboboxMultipleLabel selectedItems={value ?? []} itemToString={getLabel} />
          ) : (
            (value ?? []).map((item) => <ComboboxChip key={getKey(item)}>{getLabel(item)}</ComboboxChip>)
          )}
        </ComboboxValue>
        <ComboboxChipsInput
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={chipsProps['aria-invalid']}
        />
      </ComboboxChips>
      <ComboboxContent anchor={anchor} container={container ?? resolvedContainer}>
        <ComboboxEmpty>{showLoadingCopy ? t('combobox-loading') : (emptyMessage ?? t('combobox-empty'))}</ComboboxEmpty>
        <VirtualizedList
          items={items}
          getKey={getKey}
          getLabel={getLabel}
          estimateSize={estimateSize}
          isLoading={false}
          hasMore={false}
          isServerFilter={false}
          virtualizerRef={virtualizerRef}
        />
      </ComboboxContent>
    </Combobox>
  );
};
